/**
 * Avatares de robô para usuários bot (conexões MCP).
 *
 * A escolha é derivada do id do usuário por hash — mesmo bot, sempre o mesmo
 * robô, sem coluna nova no banco e sem upload. É o mesmo padrão que
 * `UsersFilter` já usa para a cor do chip (`chipColorsForUserId`).
 *
 * São 8 silhuetas × 8 paletas = 64 combinações, escolhidas independentemente,
 * para que dois bots da mesma empresa dificilmente se pareçam.
 *
 * SVG inline (data URI), não arquivo em `public/`: renderiza nítido em
 * qualquer tamanho, não custa requisição, e não quebra se o asset sumir do
 * build.
 */

const PALETTES = [
  { main: '#5c6bc0', dark: '#283593', bg: '#e8eaf6' }, // indigo
  { main: '#26a69a', dark: '#00695c', bg: '#e0f2f1' }, // teal
  { main: '#ffa726', dark: '#e65100', bg: '#fff3e0' }, // âmbar
  { main: '#ec407a', dark: '#ad1457', bg: '#fce4ec' }, // rosa
  { main: '#ab47bc', dark: '#6a1b9a', bg: '#f3e5f5' }, // roxo
  { main: '#29b6f6', dark: '#0277bd', bg: '#e1f5fe' }, // ciano
  { main: '#66bb6a', dark: '#2e7d32', bg: '#e8f5e9' }, // verde
  { main: '#ef5350', dark: '#c62828', bg: '#ffebee' }, // coral
];

/** Cada função devolve o miolo do SVG (o fundo circular vem do wrapper). */
const SHAPES = [
  // 1. Clássico — cabeça arredondada, antena de bolinha, visor com dois olhos.
  ({ main, dark }) => `
    <path d="M32 8v6" stroke="${main}" stroke-width="3" stroke-linecap="round"/>
    <circle cx="32" cy="7" r="3.5" fill="${main}"/>
    <rect x="13" y="27" width="4" height="11" rx="2" fill="${dark}"/>
    <rect x="47" y="27" width="4" height="11" rx="2" fill="${dark}"/>
    <rect x="16" y="14" width="32" height="30" rx="10" fill="${main}"/>
    <rect x="21" y="23" width="22" height="13" rx="6.5" fill="${dark}"/>
    <circle cx="27.5" cy="29.5" r="3" fill="#fff"/>
    <circle cx="36.5" cy="29.5" r="3" fill="#fff"/>
    <rect x="24" y="47" width="16" height="4" rx="2" fill="${main}" opacity=".5"/>`,

  // 2. Domo — topo abaulado, visor único em barra.
  ({ main, dark }) => `
    <circle cx="32" cy="8" r="3" fill="${main}"/>
    <path d="M32 11v5" stroke="${main}" stroke-width="3" stroke-linecap="round"/>
    <path d="M16 44V31a16 16 0 0 1 32 0v13a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4z" fill="${main}"/>
    <rect x="21" y="28" width="22" height="11" rx="5.5" fill="${dark}"/>
    <rect x="25" y="32" width="14" height="3" rx="1.5" fill="#fff"/>`,

  // 3. Orbe — cabeça redonda, duas antenas, sorriso.
  ({ main, dark }) => `
    <path d="M21.5 13.5l3 5" stroke="${main}" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M42.5 13.5l-3 5" stroke="${main}" stroke-width="2.5" stroke-linecap="round"/>
    <circle cx="20.5" cy="11.5" r="3" fill="${main}"/>
    <circle cx="43.5" cy="11.5" r="3" fill="${main}"/>
    <circle cx="32" cy="33" r="17" fill="${main}"/>
    <circle cx="26" cy="30" r="3.4" fill="#fff"/>
    <circle cx="38" cy="30" r="3.4" fill="#fff"/>
    <path d="M26 39q6 5 12 0" stroke="${dark}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`,

  // 4. Ciclope — um olho grande e centralizado.
  ({ main, dark }) => `
    <circle cx="32" cy="8" r="3" fill="${main}"/>
    <path d="M32 11v5" stroke="${main}" stroke-width="3" stroke-linecap="round"/>
    <rect x="17" y="15" width="30" height="34" rx="11" fill="${main}"/>
    <circle cx="32" cy="31" r="10" fill="${dark}"/>
    <circle cx="32" cy="31" r="6" fill="#fff"/>
    <circle cx="32" cy="31" r="2.8" fill="${dark}"/>`,

  // 5. Scanner — orelhas de fone, visor com três pontos.
  ({ main, dark }) => `
    <circle cx="32" cy="10" r="3" fill="${main}"/>
    <path d="M32 13v5" stroke="${main}" stroke-width="3" stroke-linecap="round"/>
    <circle cx="13.5" cy="32" r="5.5" fill="${dark}"/>
    <circle cx="50.5" cy="32" r="5.5" fill="${dark}"/>
    <rect x="15" y="18" width="34" height="28" rx="9" fill="${main}"/>
    <rect x="20" y="26" width="24" height="12" rx="6" fill="${dark}"/>
    <circle cx="27" cy="32" r="2.4" fill="#fff"/>
    <circle cx="32" cy="32" r="2.4" fill="#fff" opacity=".6"/>
    <circle cx="37" cy="32" r="2.4" fill="#fff" opacity=".3"/>`,

  // 6. Sorridente — olhos em arco.
  ({ main, dark }) => `
    <circle cx="32" cy="9" r="3.5" fill="${main}"/>
    <path d="M32 12.5v4" stroke="${main}" stroke-width="3" stroke-linecap="round"/>
    <rect x="15" y="16" width="34" height="32" rx="12" fill="${main}"/>
    <path d="M23 30.5q3.5-5.5 7 0" stroke="#fff" stroke-width="3" fill="none" stroke-linecap="round"/>
    <path d="M34 30.5q3.5-5.5 7 0" stroke="#fff" stroke-width="3" fill="none" stroke-linecap="round"/>
    <path d="M26 37q6 5.5 12 0" stroke="${dark}" stroke-width="3" fill="none" stroke-linecap="round"/>`,

  // 7. Quadradão — cantos retos, olhos quadrados, antena chata.
  ({ main, dark }) => `
    <path d="M32 10v7" stroke="${dark}" stroke-width="2.5" stroke-linecap="round"/>
    <rect x="26.5" y="5" width="11" height="5" rx="2.5" fill="${main}"/>
    <rect x="15" y="16" width="34" height="31" rx="5" fill="${main}"/>
    <rect x="21" y="24" width="9" height="9" rx="2.5" fill="#fff"/>
    <rect x="34" y="24" width="9" height="9" rx="2.5" fill="#fff"/>
    <rect x="24" y="27" width="3.5" height="3.5" rx="1" fill="${dark}"/>
    <rect x="36.5" y="27" width="3.5" height="3.5" rx="1" fill="${dark}"/>
    <rect x="24" y="38.5" width="16" height="3.5" rx="1.75" fill="${dark}"/>`,

  // 8. Cápsula — cabeça alongada, antena lateral, bochechas.
  ({ main, dark }) => `
    <circle cx="45.5" cy="12" r="3" fill="${main}"/>
    <path d="M44 14.5l-3 4.5" stroke="${main}" stroke-width="2.5" stroke-linecap="round"/>
    <rect x="18" y="16" width="28" height="33" rx="14" fill="${main}"/>
    <ellipse cx="26.5" cy="30" rx="3" ry="4" fill="#fff"/>
    <ellipse cx="37.5" cy="30" rx="3" ry="4" fill="#fff"/>
    <circle cx="21.5" cy="37" r="2.5" fill="${dark}" opacity=".45"/>
    <circle cx="42.5" cy="37" r="2.5" fill="${dark}" opacity=".45"/>
    <path d="M28 39.5h8" stroke="${dark}" stroke-width="2.5" stroke-linecap="round"/>`,
];

/** Hash estável de string → inteiro não negativo (mesmo de `chipColorsForUserId`). */
function hashId(value) {
  const s = String(value ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function toDataUri(shape, palette) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">` +
    `<circle cx="32" cy="32" r="32" fill="${palette.bg}"/>` +
    `${shape(palette)}` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' '))}`;
}

/**
 * URL (data URI) do avatar de robô de um usuário bot. Estável para o mesmo
 * `userId`. Silhueta e paleta são escolhidas de forma independente.
 */
export const getBotAvatarUrl = (userId) => {
  const h = hashId(userId);
  const shape = SHAPES[h % SHAPES.length];
  const palette = PALETTES[Math.floor(h / SHAPES.length) % PALETTES.length];
  return toDataUri(shape, palette);
};

/** Só para a galeria de pré-visualização; não usar em tela de produto. */
export const BOT_AVATAR_VARIANT_COUNT = SHAPES.length * PALETTES.length;

export default getBotAvatarUrl;
